import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import type { BoardColumn, Board, Task } from "@/types/db";

// A column the modal is tracking. `id` is null for one the user just added
// that has no database row yet; `key` is only for React's list identity.
interface ColumnDraft { id: string | null; key: string; name: string; is_done: boolean; }
// A column staged for deletion. hq_tasks.column_id cascades on delete, so a
// column holding cards can never just be dropped — `moveTo` says where its
// cards land first, and save() refuses until it is set.
interface ColumnRemoval { id: string; name: string; cardCount: number; moveTo: string | null; }

export function BoardEditModal({ board, columns, tasks, canToggleOpen = false, onClose, onSaved }: {
  board: Pick<Board, "id" | "name" | "description" | "open_to_all_staff">; columns: BoardColumn[];
  tasks: Pick<Task, "id" | "column_id" | "position">[];
  /** Administrators and the board owner. Everyone else can read the setting. */
  canToggleOpen?: boolean;
  onClose: () => void; onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description ?? "");
  const [openToAll, setOpenToAll] = useState(board.open_to_all_staff === true);
  const [drafts, setDrafts] = useState<ColumnDraft[]>(
    () => columns.map(c => ({ id: c.id, key: c.id, name: c.name, is_done: !!c.is_done })),
  );
  const [removals, setRemovals] = useState<ColumnRemoval[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cardCount = (columnId: string) => tasks.filter(t => t.column_id === columnId).length;
  // Cards can only be moved into a column that already exists in the database
  // and is surviving this edit.
  const moveTargets = drafts.filter(d => d.id) as (ColumnDraft & { id: string })[];

  function patch(key: string, next: Partial<ColumnDraft>) {
    setDrafts(prev => prev.map(d => d.key === key ? { ...d, ...next } : d));
  }

  function move(key: string, dir: -1 | 1) {
    setDrafts(prev => {
      const i = prev.findIndex(d => d.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  function addColumn() {
    setDrafts(prev => [...prev, {
      id: null,
      key: `new-${Date.now()}-${prev.length}`,
      name: "",
      is_done: false,
    }]);
  }

  function remove(draft: ColumnDraft) {
    setDrafts(prev => prev.filter(d => d.key !== draft.key));
    if (!draft.id) return; // never saved — nothing to delete
    const count = cardCount(draft.id);
    const firstOther = drafts.find(d => d.id && d.id !== draft.id)?.id ?? null;
    setRemovals(prev => [...prev, {
      id: draft.id as string,
      name: draft.name,
      cardCount: count,
      moveTo: count > 0 ? firstOther : null,
    }]);
  }

  function undoRemove(removal: ColumnRemoval) {
    const original = columns.find(c => c.id === removal.id);
    if (original) {
      setDrafts(prev => [...prev, {
        id: original.id, key: original.id, name: original.name, is_done: !!original.is_done,
      }]);
    }
    setRemovals(prev => prev.filter(r => r.id !== removal.id));
  }

  async function save() {
    const boardName = name.trim();
    if (!boardName) { setErr("Board name is required."); return; }
    if (drafts.length === 0) { setErr("A board needs at least one column."); return; }
    if (drafts.some(d => !d.name.trim())) { setErr("Every column needs a name."); return; }
    const stranded = removals.find(r => r.cardCount > 0 && !r.moveTo);
    if (stranded) {
      setErr(`Choose where the ${stranded.cardCount} card${stranded.cardCount === 1 ? "" : "s"} in “${stranded.name}” should go.`);
      return;
    }
    setBusy(true); setErr(null);

    // 1. Board name / description.
    // Send the flag only when it changed. A rename before the column exists
    // still saves, and a member who cannot toggle it never writes it.
    const boardPatch: { name: string; description: string | null; open_to_all_staff?: boolean } = {
      name: boardName,
      description: description.trim() || null,
    };
    if (canToggleOpen && openToAll !== (board.open_to_all_staff === true)) {
      boardPatch.open_to_all_staff = openToAll;
    }
    const boardRes = await supabase.from("hq_task_boards")
      .update(boardPatch)
      .eq("id", board.id);
    if (boardRes.error) { setBusy(false); setErr(boardRes.error.message); return; }

    // 2. Removals — relocate the cards first, then drop the column. Doing it
    //    in this order means the delete cascade never reaches a live card.
    for (const r of removals) {
      if (r.moveTo) {
        const moving = tasks.filter(t => t.column_id === r.id)
          .sort((a, b) => Number(a.position) - Number(b.position));
        const existing = tasks.filter(t => t.column_id === r.moveTo).map(t => Number(t.position));
        const base = existing.length ? Math.max(...existing) + 1 : 0;
        for (let i = 0; i < moving.length; i++) {
          const res = await supabase.from("hq_tasks")
            .update({ column_id: r.moveTo, position: base + i })
            .eq("id", moving[i].id);
          if (res.error) { setBusy(false); setErr(`Moving cards out of “${r.name}” failed: ${res.error.message}`); return; }
        }
      }
      const del = await supabase.from("hq_task_columns").delete().eq("id", r.id);
      if (del.error) { setBusy(false); setErr(`Deleting “${r.name}” failed: ${del.error.message}`); return; }
    }

    // 3. Renames, done-flags and reordering for columns that already exist.
    for (let i = 0; i < drafts.length; i++) {
      const d = drafts[i];
      if (!d.id) continue;
      const original = columns.find(c => c.id === d.id);
      const unchanged = original
        && original.name === d.name.trim()
        && !!original.is_done === d.is_done
        && Number(original.position) === i;
      if (unchanged) continue;
      const res = await supabase.from("hq_task_columns")
        .update({ name: d.name.trim(), is_done: d.is_done, position: i })
        .eq("id", d.id);
      if (res.error) { setBusy(false); setErr(`Saving “${d.name}” failed: ${res.error.message}`); return; }
    }

    // 4. Brand-new columns.
    const added = drafts
      .map((d, i) => ({ d, i }))
      .filter(({ d }) => !d.id)
      .map(({ d, i }) => ({ board_id: board.id, name: d.name.trim(), position: i, is_done: d.is_done }));
    if (added.length) {
      const res = await supabase.from("hq_task_columns").insert(added);
      if (res.error) { setBusy(false); setErr(`Adding columns failed: ${res.error.message}`); return; }
    }

    setBusy(false);
    await onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="mb-3 text-lg font-semibold">Edit board</h2>
        {err && <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{err}</div>}
        <div className="space-y-3">
          <div>
            <label className="label">Name</label>
            <input className="input" aria-label="Board name" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" aria-label="Board description" rows={3} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          {canToggleOpen ? (
            <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg bg-offwhite p-3 ring-1 ring-line">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 shrink-0"
                checked={openToAll}
                onChange={e => setOpenToAll(e.target.checked)}
                aria-label="Open to all HQ staff"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Open to all HQ staff</span>
                <span className="mt-0.5 block text-xs leading-snug text-gray2">
                  Every active employee can open this board, including people hired later.
                  Someone who is not active does not get in this way.
                </span>
              </span>
            </label>
          ) : openToAll ? (
            <p className="rounded-lg bg-offwhite p-3 text-sm text-gray2 ring-1 ring-line">
              This board is open to every active HQ employee. An administrator or the owner can change that.
            </p>
          ) : null}

          <div>
            <label className="label">Columns</label>
            <div className="space-y-2">
              {drafts.map((d, i) => (
                <div key={d.key} className="flex items-center gap-2 rounded-lg bg-offwhite p-2 ring-1 ring-line">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => move(d.key, -1)}
                      disabled={i === 0}
                      className="rounded px-1 text-gray2 hover:text-teal-dark disabled:opacity-30"
                      title="Move left"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(d.key, 1)}
                      disabled={i === drafts.length - 1}
                      className="rounded px-1 text-gray2 hover:text-teal-dark disabled:opacity-30"
                      title="Move right"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <input
                    className="input flex-1"
                    value={d.name}
                    placeholder="Column name"
                    onChange={e => patch(d.key, { name: e.target.value })}
                  />
                  <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray2" title="Cards here count as finished">
                    <input
                      type="checkbox"
                      checked={d.is_done}
                      onChange={e => patch(d.key, { is_done: e.target.checked })}
                    />
                    Done
                  </label>
                  <span className="w-14 shrink-0 text-right text-xs text-gray2">
                    {d.id ? `${cardCount(d.id)} card${cardCount(d.id) === 1 ? "" : "s"}` : "new"}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(d)}
                    className="shrink-0 rounded p-1 text-gray2 hover:text-bad"
                    title="Remove column"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addColumn} className="btn btn-ghost btn-sm mt-2 flex items-center gap-1">
              <Plus size={14} /> Add column
            </button>
          </div>

          {removals.length > 0 && (
            <div className="rounded-lg bg-warn-bg p-2 ring-1 ring-line">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray2">
                Removed when you save
              </div>
              <div className="space-y-2">
                {removals.map(r => (
                  <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium line-through">{r.name}</span>
                    {r.cardCount > 0 && moveTargets.length === 0 ? (
                      <span className="text-xs text-bad">
                        holds {r.cardCount} card{r.cardCount === 1 ? "" : "s"} and there is no other
                        saved column to move them to — undo, or save the new columns first
                      </span>
                    ) : r.cardCount > 0 ? (
                      <>
                        <span className="text-xs text-gray2">
                          move {r.cardCount} card{r.cardCount === 1 ? "" : "s"} to
                        </span>
                        <select
                          className="input h-8 w-40 py-0 text-sm"
                          aria-label={`Move the cards in ${r.name} to`}
                          value={r.moveTo ?? ""}
                          onChange={e => setRemovals(prev => prev.map(x =>
                            x.id === r.id ? { ...x, moveTo: e.target.value || null } : x))}
                        >
                          <option value="">Choose a column…</option>
                          {moveTargets.map(t => (
                            <option key={t.id} value={t.id}>{t.name || "(unnamed)"}</option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <span className="text-xs text-gray2">empty</span>
                    )}
                    <button type="button" onClick={() => undoRemove(r)} className="btn btn-ghost btn-sm">
                      Undo
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost" disabled={busy}>Cancel</button>
          <button onClick={save} className="btn" disabled={busy || !name.trim()}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
